import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Card } from '@safetyhub/ui';
import { apiErrorMessage } from '../../lib/api';
import {
  fetchAnnouncementBoard,
  markAnnouncementSeen,
  type AnnouncementBoardItem,
} from '../../lib/announcements';
import { useEntityChanged } from '../../hooks/useEntityChanged';
import { useToastStore } from '../../stores/toast';

/**
 * 공지 게시판 (`/announcements`) — 줄기 `popup-and-announcement-surface` P4-b.
 *
 * ## 이 화면이 소유하는 축은 **보존**이다
 *
 * 팝업·고정 띠는 **노출**이고 이 목록은 **보존**이라 서로를 대체하지 않는다 — pcon ADR-025가
 * 「앱 홈 카드만」을 기각한 근거가 **「읽고 나면 사라져 «계속 봐야 하는 정보»가 다시 묻힌다」**이고,
 * 그래서 **지난 공지도 여기 남는다**(기간이 지났거나 이미 읽은 것). 서버가 라우트를 둘로 가른
 * 이유가 그것이고(`GET /announcements` ↔ `/announcements/pinned`) 클라이언트도 함수를 가른다
 * (`lib/announcements.ts` 헤더 JSDoc).
 *
 * ## 전사와 조직 공지가 **한 페이지**에 온다 — 구분은 배지다
 *
 * 사용자가 둘을 가려 찾지 않아도 되게 한 자리에 둔다(줄기 P4-b). 가름은 `organizationId`뿐이다 —
 * ⚠️ **뷰어 계약에 조직 «이름»이 없다**(`AnnouncementBoardItem`이 저작 축과 함께 일부러 뺐다). 게다가
 * 서버 스코프는 **내 멤버십 전부**에서 파생되므로(`membershipScope`) 조직이 둘인 계정은 양쪽 공지를
 * 함께 받는다 ⇒ 배지를 「우리 조직」처럼 **한 조직을 가리키는 말로 쓰지 말 것**(그 계정에 거짓이 된다).
 *
 * ## 읽음은 **본문을 펼칠 때** 보낸다
 *
 * 목록에 보인 것만으로 읽음을 찍으면 「읽지 않음」 배지가 **스크롤 한 번에 전부 꺼진다** — 그 배지의
 * 뜻이 사라진다. 그래서 펼치는 행위(= 본문을 본다)가 기준이고, 실패하면 배지를 **되돌린다**(서버에
 * 안 남았는데 꺼져 있으면 다음 접속에 다시 켜져 더 혼란스럽다).
 *
 * ⚠️ **이 화면의 spec은 레이아웃을 재지 않는다** — jsdom에 레이아웃 엔진이 없어
 * `getBoundingClientRect()`가 0을 돌려주므로 여백·정렬·줄바꿈은 **그 초록이 보증하지 않는다**
 * (그 축은 `docs/verification/` 캡처의 몫이다).
 */
export default function AnnouncementsPage() {
  const { showToast } = useToastStore();
  const [items, setItems] = useState<AnnouncementBoardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  /**
   * 「읽음」 쓰기가 **날아가는 중인 건수** — `useEntityChanged`의 `paused`가 읽는 dirty 축이다.
   * 이것이 `useRef`가 아니라 `useState`인 것이 요점이다: `paused`는 **렌더 때 읽히므로** ref로
   * 두면 0으로 돌아온 것이 훅에 전달되지 않아 미뤄 둔 재조회가 **영영 안 돈다**.
   */
  const [seenInFlight, setSeenInFlight] = useState(0);
  /**
   * 조회 회차 — **마지막 요청의 응답만** 반영한다. 종전 `alive` 플래그가 막던 것은 언마운트 뒤의
   * 늦은 응답뿐이었고(deps `[]`), 실시간 재조회가 붙으면서 **조회가 겹치는** 축이 생겼다.
   */
  const requestRef = useRef(0);

  // silent: 실시간 재조회(`ENTITY_CHANGED`) 경로 — 목록을 「불러오는 중」 스피너로 갈아 끼우지
  // 않는다. 그 토글은 아래 렌더의 `loading ? …`에서 목록을 통째로 지우므로, 남이 공지를 하나
  // 고칠 때마다 보던 목록이 사라지고 **펼쳐 둔 본문도 화면에서 없어진다**.
  const load = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    const seq = ++requestRef.current;
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const rows = await fetchAnnouncementBoard();
      if (requestRef.current !== seq) return;
      setItems(rows);
      setError(null);
    } catch (err) {
      if (requestRef.current !== seq) return;
      // 실시간 재조회의 실패는 보던 목록을 에러 화면으로 갈아 끼우지 않는다 — 목록은 직전 값으로
      // 여전히 유효하고, 다음 변경 알림·새로고침이 다시 시도한다(리뷰 1103).
      if (silent) return;
      setError(apiErrorMessage(err) ?? '공지를 불러오지 못했습니다.');
    } finally {
      if (!silent && requestRef.current === seq) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * 남이 공지를 쓰면 목록을 다시 받는다 — 서버 `ENTITY_CHANGED(announcement)`(줄기 P9, 2026-10-27).
   * 이 화면은 react-query를 쓰지 않으므로 무효화가 닿지 않는다.
   *
   * ## ⚠️ `paused` 축이 **모달이 아니다** — 「읽음」 쓰기가 날아가는 중이다
   *
   * 이 화면에는 폼·편집 모달이 **없다**(입력 요소 0 — 토글 버튼뿐이다). 그런데 덮어쓸 로컬 쓰기가
   * 하나 있다: `handleToggle`이 `markAnnouncementSeen`을 **보내기 전에** 배지를 먼저 끈다(낙관적
   * 갱신). 그 사이에 재조회가 끝나면 서버는 봤음 행을 **아직 쓰지 않았으므로** `unread: true`를
   * 돌려주고, 배지가 **다시 켜진 채 남는다**(그 쓰기가 성공해도 상태를 다시 쓰지 않으므로 다음
   * 재조회까지 거짓이다). ⇒ 그 창 동안만 미룬다. 창이 짧다고 안 걸지 말 것 — 틀리면 **「읽었는데
   * 읽지 않음으로 보인다」**가 지속되고, 그 배지의 뜻이 이 화면의 보존 축이다.
   *
   * ⚠️ `expandedId`는 이 축이 **아니다** — 값이 id 문자열이라 목록이 바뀌어도 그 행이 남아 있으면
   * 펼침이 유지되고, 그 행이 사라져 접히는 것은 **옳은 결과**다.
   *
   * ⚠️ 전사 공지는 이 이벤트로 오지 않는다 — 그런데 이 화면은 전사 공지도 **보므로** 그 공백이
   * 여기서는 실제 손해다(전사 공지는 다음 진입·새로고침에만 나타난다). 사유·되짚을 조건은 서버
   * `AnnouncementAuthoringService.emitChanged` JSDoc과 줄기 P9 항목이 정본이다.
   */
  useEntityChanged(['announcement'], () => void load({ silent: true }), {
    paused: seenInFlight > 0,
  });

  const setUnread = useCallback((id: string, unread: boolean) => {
    setItems((prev) => prev.map((row) => (row.id === id ? { ...row, unread } : row)));
  }, []);

  const handleToggle = useCallback(
    async (item: AnnouncementBoardItem) => {
      const opening = expandedId !== item.id;
      setExpandedId(opening ? item.id : null);
      // 접을 때는 아무것도 보내지 않는다 — 읽음은 한 번 참이 되면 되돌릴 일이 없다(서버 upsert).
      if (!opening || !item.unread) return;
      setUnread(item.id, false);
      // 이 창 동안 실시간 재조회를 미룬다(위 `paused` JSDoc) — 서버가 봤음 행을 쓰기 전에 목록을
      // 다시 받으면 방금 끈 배지가 다시 켜진 채 남는다.
      setSeenInFlight((n) => n + 1);
      try {
        await markAnnouncementSeen(item.id);
      } catch (err) {
        // 되돌린다 — 서버에 안 남은 읽음을 꺼 두면 다음 접속에 다시 켜져 더 혼란스럽다
        setUnread(item.id, true);
        showToast(apiErrorMessage(err) ?? '읽음 처리에 실패했습니다.', 'error');
      } finally {
        setSeenInFlight((n) => n - 1);
      }
    },
    [expandedId, setUnread, showToast],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-ink-900">공지</h1>
        <p className="mt-1 text-sm text-ink-600">
          전사 공지와 조직 공지를 한자리에서 봅니다. 기간이 지난 공지도 남아 있습니다.
        </p>
      </div>

      {loading ? (
        <div className="py-12 text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-brand-600" />
          <p className="mt-4 text-ink-500">불러오는 중...</p>
        </div>
      ) : error ? (
        <div className="py-12 text-center">
          <p className="text-rose-600">{error}</p>
        </div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-ink-500">공지가 없습니다.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((item) => {
            const expanded = expandedId === item.id;
            return (
              <li key={item.id}>
                <Card padding="none" radius="lg" className="overflow-hidden">
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => void handleToggle(item)}
                    className="w-full px-5 py-4 text-left hover:bg-ink-50"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      {/* 전사 ↔ 조직 — 가름은 `organizationId` 하나다(위 JSDoc: 조직 이름은 계약에 없다) */}
                      {item.organizationId === null ? (
                        <Badge tone="violet">전사 공지</Badge>
                      ) : (
                        <Badge tone="blue">조직 공지</Badge>
                      )}
                      {item.pinned && <Badge tone="amber">고정</Badge>}
                      {/* 「읽지 않음」은 **존재가 상태를 뜻하는 요소**라 조건부 렌더다
                          (`hidden` 클래스로 숨기면 손실이 초록으로 난다) */}
                      {item.unread && <Badge tone="rose">읽지 않음</Badge>}
                      {item.expired && <Badge tone="slate">지난 공지</Badge>}
                    </div>
                    <p
                      className={`mt-2 font-semibold ${
                        item.expired ? 'text-ink-500' : 'text-ink-900'
                      }`}
                    >
                      {item.title}
                    </p>
                    <p className="mt-1 text-xs text-ink-400">
                      {new Date(item.publishedAt).toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </button>
                  {expanded && (
                    // 서버에서 온 문자열이라 **텍스트로만** 그린다(`dangerouslySetInnerHTML` 금지)
                    <div className="border-t border-line px-5 py-4">
                      <p className="whitespace-pre-line text-sm text-ink-700">{item.body}</p>
                    </div>
                  )}
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
